import {
  addDays,
  addMonths,
  addWeeks,
  min,
  max,
  parseISO,
  eachDayOfInterval,
  eachWeekOfInterval,
  startOfWeek,
  endOfWeek,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  eachMonthOfInterval,
  startOfMonth,
  endOfMonth,
  isMonday,
  isSunday,
  isFirstDayOfMonth,
  isLastDayOfMonth,
} from 'date-fns';
import type { PlanResolution } from '../../types';
import type { PlanRow } from 'modules/campaign/row';
import type { Segment, SegmentHandle, Interval, PositionError, Calendar, CalendarColumn } from './types';
import { HANDLE_WIDTH, UNDERSIZED_WIDTH } from './const';

export function calculateBounds<T extends { width: number }>(
  columns: T[],
): Array<
  T & {
    left: number;
    right: number;
  }
> {
  let curLeft = 0;
  return columns.map((col) => {
    const left = curLeft;
    curLeft += col.width + 1;

    return {
      ...col,
      left,
      right: left + col.width,
    };
  });
}

export function isAligned(start: Date, end: Date, resolution: PlanResolution): boolean {
  switch (resolution) {
    case 'DAY':
      return true;
    case 'WEEK':
      return isMonday(start) && isSunday(end);
    case 'MONTH':
      return isFirstDayOfMonth(start) && isLastDayOfMonth(end);
  }
}

export function countWorkUnits({ start, end }: Interval, resolution: PlanResolution): number {
  switch (resolution) {
    case 'DAY':
      return differenceInDays(end, start) + 1;
    case 'WEEK':
      return differenceInWeeks(startOfWeek(end, { weekStartsOn: 1 }), startOfWeek(start, { weekStartsOn: 1 })) + 1;
    case 'MONTH':
      return differenceInMonths(endOfMonth(end), startOfMonth(start)) + 1;
  }
}

export function computeWorkUnitDelta(initialDay: Date, currentDay: Date, resolution: PlanResolution): number {
  switch (resolution) {
    case 'DAY':
      return differenceInDays(currentDay, initialDay);
    case 'WEEK':
      return differenceInWeeks(
        startOfWeek(currentDay, { weekStartsOn: 1 }),
        startOfWeek(initialDay, { weekStartsOn: 1 }),
      );
    case 'MONTH':
      return differenceInMonths(startOfMonth(currentDay), startOfMonth(initialDay));
  }
}

function shiftStartByUnits(start: Date, unitDelta: number, resolution: PlanResolution): Date {
  switch (resolution) {
    case 'DAY':
      return addDays(start, unitDelta);
    case 'WEEK':
      return addWeeks(start, unitDelta);
    case 'MONTH':
      return startOfMonth(addMonths(start, unitDelta));
  }
}

function computeEndFromStartAndUnits(start: Date, unitCount: number, resolution: PlanResolution): Date {
  switch (resolution) {
    case 'DAY':
      return addDays(start, unitCount - 1);
    case 'WEEK':
      return endOfWeek(addWeeks(start, unitCount - 1), { weekStartsOn: 1 });
    case 'MONTH':
      return endOfMonth(addMonths(start, unitCount - 1));
  }
}

export function shiftIntervalByWorkUnits(
  { start, end }: Interval,
  handle: SegmentHandle,
  unitDelta: number,
  resolution: PlanResolution,
  bounds?: Interval,
): Interval & { unitDelta: number } {
  if (unitDelta === 0) {
    return { start, end, unitDelta: 0 };
  }

  let newStart: Date;
  let newEnd: Date;

  switch (handle) {
    case 'middle': {
      const unitCount = countWorkUnits({ start, end }, resolution);
      newStart = shiftStartByUnits(start, unitDelta, resolution);
      newEnd = computeEndFromStartAndUnits(newStart, unitCount, resolution);

      if (bounds) {
        if (newStart < bounds.start) {
          newStart = shiftStartByUnits(bounds.start, 0, resolution);
          if (resolution === 'MONTH') newStart = startOfMonth(bounds.start);
          else if (resolution === 'WEEK')
            newStart = max([startOfWeek(bounds.start, { weekStartsOn: 1 }), bounds.start]);
          else newStart = bounds.start;
          newEnd = computeEndFromStartAndUnits(newStart, unitCount, resolution);
        }
        if (newEnd > bounds.end) {
          if (resolution === 'MONTH') newEnd = endOfMonth(bounds.end);
          else if (resolution === 'WEEK') newEnd = min([endOfWeek(bounds.end, { weekStartsOn: 1 }), bounds.end]);
          else newEnd = bounds.end;
          newStart = shiftStartByUnits(newEnd, -(unitCount - 1), resolution);
          if (resolution === 'MONTH') newStart = startOfMonth(addMonths(newEnd, -(unitCount - 1)));
          else if (resolution === 'WEEK')
            newStart = startOfWeek(addWeeks(newEnd, -(unitCount - 1)), { weekStartsOn: 1 });
        }
        newStart = max([newStart, bounds.start]);
        newEnd = min([newEnd, bounds.end]);
      }
      break;
    }
    case 'left': {
      newStart = shiftStartByUnits(start, unitDelta, resolution);
      newEnd = end;
      const endUnitStart =
        resolution === 'MONTH'
          ? startOfMonth(end)
          : resolution === 'WEEK'
            ? startOfWeek(end, { weekStartsOn: 1 })
            : end;
      if (newStart > endUnitStart) {
        newStart = endUnitStart;
      }
      if (bounds && newStart < bounds.start) {
        newStart = bounds.start;
      }
      break;
    }
    case 'right': {
      newStart = start;
      newEnd = computeEndFromStartAndUnits(end, 1 + unitDelta, resolution);
      const startUnitEnd =
        resolution === 'MONTH'
          ? endOfMonth(start)
          : resolution === 'WEEK'
            ? endOfWeek(start, { weekStartsOn: 1 })
            : start;
      if (newEnd < startUnitEnd) {
        newEnd = startUnitEnd;
      }
      if (bounds && newEnd > bounds.end) {
        newEnd = bounds.end;
      }
      break;
    }
  }

  return { start: newStart, end: newEnd, unitDelta };
}

export function calcRowSegments(positions: PlanRow['positions'], calendar: Calendar): Segment[] {
  return positions.map((pos) => {
    const { date_from, date_to } = pos;
    const start = parseISO(date_from);
    const end = parseISO(date_to);
    const bounds = calendar.intervalToBounds({ start, end });
    return {
      ...pos,
      start,
      end,
      ...bounds,
      undersized: bounds.width < UNDERSIZED_WIDTH,
      aligned: isAligned(start, end, calendar.resolution),
    };
  });
}

export function getSegmentMouseHit(
  mouseX: number,
  rowSegments: Segment[],
): [segment: Segment, handle: SegmentHandle] | null {
  if (rowSegments.length > 0) {
    const segment = rowSegments.find((segment) => segment.left <= mouseX && segment.right >= mouseX);
    if (segment) {
      if (segment.undersized) {
        return [segment, 'middle'];
      }
      const handle =
        segment.left + HANDLE_WIDTH > mouseX ? 'left' : segment.right - HANDLE_WIDTH < mouseX ? 'right' : 'middle';
      return [segment, handle];
    }
  }
  return null;
}

export function getPositionError(segments: Segment[], interval: Interval, excludeId?: number): PositionError | null {
  let { start, end } = interval;
  if (segments.length > 0) {
    // @IMPORTANT asserts segments are ordered
    for (let i = 0; i < segments.length; i++) {
      const { id, start: segmentStart, end: segmentEnd } = segments[i];
      if (id === excludeId) {
        continue;
      }

      if (segmentStart < start) {
        start = segmentStart;
      }
      if (segmentEnd > end) {
        end = segmentEnd;
      }

      if (segmentEnd < interval.start) {
        continue;
      }
      if (segmentStart > interval.end) {
        break;
      }
      return 'overlap';
    }
  }

  return null;
}

export const dayScale = (interval: Interval, dayWidth: number): CalendarColumn[] =>
  calculateBounds(eachDayOfInterval(interval).map((date) => ({ start: date, end: date, width: dayWidth })));

export const weekScale = (interval: Interval, dayWidth: number): CalendarColumn[] =>
  calculateBounds(
    eachWeekOfInterval(interval, { weekStartsOn: 1 }).map((date) => {
      const start = max([interval.start, date]);
      const end = min([interval.end, endOfWeek(date, { weekStartsOn: 1 })]);

      const lengthInDays = differenceInDays(end, start) + 1; // inclusive
      const width = lengthInDays * dayWidth + (lengthInDays - 1);
      return {
        start,
        end,
        width,
      };
    }),
  );

export const monthScale = (interval: Interval, dayWidth: number): CalendarColumn[] =>
  calculateBounds(
    eachMonthOfInterval(interval).map((date) => {
      const start = max([interval.start, date]);
      const end = min([interval.end, endOfMonth(date)]);
      const lengthInDays = differenceInDays(end, start) + 1; // inclusive
      const width = lengthInDays * dayWidth + (lengthInDays - 1);
      return {
        start,
        end,
        width,
      };
    }),
  );

export function dateToInterval(date: Date, resolution: PlanResolution, bounds: Interval): Interval {
  switch (resolution) {
    case 'DAY':
      return { start: date, end: date };
    case 'WEEK':
      return {
        start: max([startOfWeek(date, { weekStartsOn: 1 }), bounds.start]),
        end: min([endOfWeek(date, { weekStartsOn: 1 }), bounds.end]),
      };
    case 'MONTH':
      return {
        start: max([startOfMonth(date), bounds.start]),
        end: min([endOfMonth(date), bounds.end]),
      };
  }
}
